import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  TableContainer,
  Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

interface VendorLicensePlateLookupProps {
  vendorId?: string;
  // Admin mode searches every site at once and shows which site each session
  // belongs to. Vendor mode is confined to the one vendorId.
  admin?: boolean;
}

export default function VendorLicensePlateLookup({ vendorId, admin }: VendorLicensePlateLookupProps) {
  const [licensePlate, setLicensePlate] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [searchedPlate, setSearchedPlate] = useState('');
  const [error, setError] = useState('');

  const handleSearch = async () => {
    if (!licensePlate.trim()) {
      setError('Please enter a license plate');
      return;
    }

    setLoading(true);
    setError('');
    setResults([]);

    try {
      // Fetch the partner's sessions through the Cloud Function (a vendor
      // cannot read the sessions collection directly), then match the plate.
      const fn = httpsCallable(functions, 'getPartnerReport');
      const res: any = await fn({
        report: 'rawsessions',
        vendorId: admin ? '__all__' : vendorId,
      });
      const sessions: any[] = (res.data && res.data.sessions) || [];
      const q = licensePlate.toUpperCase().trim();
      // Match on the plate OR the vehicle nickname. When a plate is changed
      // while a session is active the stored plate can come back blank, so the
      // nickname (e.g. "Wild Thang") is the reliable way to still find the car.
      const matches = sessions.filter((s: any) => {
        const p = (s.vehicleInfo?.licensePlate || '').toUpperCase();
        const n = (s.vehicleInfo?.nickname || '').toUpperCase();
        return (p && (p === q || p.includes(q))) || (n && (n === q || n.includes(q)));
      });
      if (matches.length === 0) {
        setError('No session found at your parking lots for that plate or vehicle name');
      } else {
        // Show all of this plate's sessions, most recent first.
        matches.sort(
          (a: any, b: any) => (b.startTime?.seconds || 0) - (a.startTime?.seconds || 0)
        );
        setResults(matches);
        setSearchedPlate(q);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to search. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: any): string => {
    if (!timestamp) return 'N/A';
    try {
      if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleString();
      }
      return new Date(timestamp).toLocaleString();
    } catch {
      return 'N/A';
    }
  };

  const calculateTimeParked = (startTimestamp: any): string => {
    try {
      let startDateTime: Date;
      if (startTimestamp.seconds) {
        startDateTime = new Date(startTimestamp.seconds * 1000);
      } else {
        startDateTime = new Date(startTimestamp);
      }
      
      const now = new Date();
      const diffMs = now.getTime() - startDateTime.getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      return `${diffHours}h ${diffMinutes}m`;
    } catch {
      return 'N/A';
    }
  };

  const isOverstayed = (startTimestamp: any, prepaidMinutes: number): boolean => {
    try {
      let startDateTime: Date;
      if (startTimestamp.seconds) {
        startDateTime = new Date(startTimestamp.seconds * 1000);
      } else {
        startDateTime = new Date(startTimestamp);
      }
      
      const now = new Date();
      const diffMinutes = (now.getTime() - startDateTime.getTime()) / (1000 * 60);
      return diffMinutes > prepaidMinutes;
    } catch {
      return false;
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        License Plate Lookup
      </Typography>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <TextField
            fullWidth
            label="License Plate or Vehicle Name"
            value={licensePlate}
            onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
            placeholder="SHEBHOT  or  WILD THANG"
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading}
            startIcon={<SearchIcon />}
            sx={{ minWidth: 120 }}
          >
            {loading ? 'Searching...' : 'Search'}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {results.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {results.length} session{results.length === 1 ? '' : 's'} for {searchedPlate}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>License Plate</TableCell>
                  <TableCell>Date / Time</TableCell>
                  {admin && <TableCell>Site</TableCell>}
                  <TableCell>Parking Lot</TableCell>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>User Email</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Prepaid</TableCell>
                  <TableCell>Time Parked</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((r: any, i: number) => (
                  <TableRow key={i}>
                    <TableCell>
                      <strong>
                        {r.vehicleInfo?.licensePlate ||
                          (r.vehicleInfo?.nickname ? '(no plate)' : 'N/A')}
                      </strong>
                      {r.vehicleInfo?.nickname && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {r.vehicleInfo.nickname}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatTimestamp(r.startTime)}</TableCell>
                    {admin && <TableCell>{r.partnerName || 'N/A'}</TableCell>}
                    <TableCell>{r.lotName || 'N/A'}</TableCell>
                    <TableCell>
                      {[r.vehicleInfo?.color, r.vehicleInfo?.make, r.vehicleInfo?.model]
                        .filter(Boolean)
                        .join(' ') || 'N/A'}
                    </TableCell>
                    <TableCell>{r.customerInfo?.email || r.userEmail || 'N/A'}</TableCell>
                    <TableCell align="right">${(Number(r.amount) || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">
                      {r.prepaidMinutes
                        ? `${Math.floor(r.prepaidMinutes / 60)}h ${r.prepaidMinutes % 60}m`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {r.sessionActive ? calculateTimeParked(r.startTime) : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={r.sessionActive && r.prepaidMinutes && isOverstayed(r.startTime, r.prepaidMinutes)
                          ? 'OVERSTAYED'
                          : r.sessionActive ? 'Active' : 'Complete'}
                        color={r.sessionActive && r.prepaidMinutes && isOverstayed(r.startTime, r.prepaidMinutes)
                          ? 'error' : r.sessionActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
